/**
 * Pool Intents — the open (unresolved) two-phase intents queue. Each row is a sweep write that called
 * (or was about to call) Zoom and never got its completion write — usually because the worker crashed
 * mid-call. `stale` (open >~10 minutes) is a strong crash signal, so those rows are highlighted rather
 * than buried in the same list as a call that may still genuinely be in flight. Resolving is the only
 * way out of this list; nothing here auto-expires or auto-resolves.
 *
 * rev 6 (R2/R7): this whole endpoint is `_manage`-gated (owner/admin pool role) — there is no read-only
 * "pool-Viewer looks but can't resolve" state here the way PeriodModal has one, because a viewer never
 * reaches this screen at all (no tab, and a direct-URL 403 renders <PoolErrorState/> below instead of a
 * table). So every row that DOES render here is always resolvable — no per-button gate needed.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, EmptyState, SkeletonRows, StatusPill, Table, type Column } from "@/components/ui";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import { ResolveIntentDialog } from "@/features/pool/ResolveIntentDialog";
import { INTENT_ACTION_LABEL_KEY } from "@/features/pool/intentValidation";
import { usePoolIntents } from "@/features/pool/hooks";
import { formatInstant } from "@/lib/format";
import type { PoolIntent } from "@/api/types";

export function PoolIntentsPage() {
  const { t } = useTranslation();
  const intentsQ = usePoolIntents();
  const [selected, setSelected] = useState<PoolIntent | null>(null);

  const columns: Column<PoolIntent>[] = [
    {
      key: "ts",
      header: t("pool.intents.colTs"),
      numeric: true,
      sortValue: (r) => r.ts,
      cell: (r) => formatInstant(r.ts),
    },
    {
      key: "action",
      header: t("pool.intents.colAction"),
      sortValue: (r) => r.action,
      cell: (r) => t(INTENT_ACTION_LABEL_KEY[r.action]),
    },
    {
      key: "email",
      header: t("pool.intents.colEmail"),
      sortValue: (r) => r.target_email ?? "",
      cell: (r) => <span className="font-medium text-(--text)">{r.target_email ?? "—"}</span>,
    },
    {
      key: "trigger",
      header: t("pool.intents.colTrigger"),
      sortValue: (r) => r.trigger,
      cell: (r) => r.trigger,
    },
    {
      key: "status",
      header: t("pool.intents.colStatus"),
      cell: (r) =>
        r.stale ? (
          <StatusPill tone="danger">{t("pool.intents.staleWarning")}</StatusPill>
        ) : (
          <StatusPill tone="neutral">{t("pool.intents.pendingLabel")}</StatusPill>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <Button size="sm" variant="secondary" onClick={() => setSelected(r)}>
          {t("pool.intents.resolveButton")}
        </Button>
      ),
    },
  ];

  return (
    <section aria-labelledby="pool-intents-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <h1
          id="pool-intents-title"
          className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
        >
          {t("pool.intents.title")}
        </h1>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.intents.subtitle")}</p>
      </header>

      {intentsQ.isPending ? (
        <SkeletonRows rows={4} />
      ) : intentsQ.isError ? (
        <PoolErrorState error={intentsQ.error} onRetry={() => void intentsQ.refetch()} />
      ) : (
        <Table
          columns={columns}
          rows={intentsQ.data.intents}
          rowKey={(r) => String(r.id)}
          caption={t("pool.intents.title")}
          // Oldest first: the longer an intent has sat open, the more likely it's a genuine crash (see
          // `stale`) and the more urgent it is to resolve — not a recency feed.
          initialSort={{ key: "ts", dir: "asc" }}
          rowClassName={(r) => (r.stale ? "bg-(--danger-subtle)" : undefined)}
          empty={
            <EmptyState title={t("pool.intents.emptyTitle")} body={t("pool.intents.emptyBody")} />
          }
        />
      )}

      {selected && <ResolveIntentDialog intent={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
